import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface OptionalDataEnvelope<T> {
  code: 'success'
  data?: T
  message?: string
}

export interface AdminAccount {
  created_at?: string | null
  email: string
  id: string
  interface_language?: string | null
  last_active_at?: string | null
  name: string
  role: string
  status: 'active' | 'banned'
  timezone?: string | null
  updated_at?: string | null
}

export interface AdminAccountsParams {
  order?: 'asc' | 'desc'
  orderBy?: string
  page: number
  pageSize: number
  search?: string
  status?: 'active' | 'banned' | ''
}

export interface AdminAccountsData {
  items: AdminAccount[]
  page: number
  page_size: number
  total: number
}

export interface AdminAccountCreateResult extends AdminAccount {
  generated_password?: string
}

export interface AdminAccountPasswordResetResult {
  generated_password?: string
}

export interface CoreAgentModelOption {
  id: string
  interface_type?: string
  label: string
  model_name?: string
  provider?: string
  public_model_name?: string
}

export interface CoreAgentSettings {
  allowed_registered_model_ids: string[]
  default_registered_model_id: string
  enabled: boolean
  models?: CoreAgentModelOption[]
  show_tool_invocation_details: boolean
  system_prompt: string
  updated_at?: string | null
  updated_by?: string | null
}

export interface CoreAgentSettingsPayload {
  allowed_registered_model_ids: string[]
  default_registered_model_id: string
  enabled: boolean
  show_tool_invocation_details: boolean
  system_prompt: string
}

export type SsoType = 'cas' | 'oauth2' | 'oidc' | 'saml'

export type SsoProtocolConfig = Record<string, string | null | undefined>

export interface AdminSsoConfig {
  email_password_enabled: boolean
  id: string | null
  sso_login_behavior: 'button' | 'redirect'
  sso_config: SsoProtocolConfig
  sso_type: SsoType | null
  updated_at?: string | null
  web_sso_enabled: boolean
}

export interface AdminSsoConfigPayload {
  email_password_enabled: boolean
  sso_login_behavior: 'button' | 'redirect'
  sso_config?: SsoProtocolConfig
  sso_type: SsoType | null
  web_sso_enabled: boolean
}

export interface AdminSsoTestResult {
  details?: string[]
  message: string
  status: 'success' | 'warning'
}

export interface SsoIdentityMatchSource {
  attribute: string
  label: string
  value_type: 'string'
}

export interface OidcProviderConfiguration {
  authorization_endpoint: string
  code_challenge_method: string
  discovery_url: string
  enabled: boolean
  introspection_endpoint: string
  issuer: string
  jwks_uri: string
  oauth_discovery_url: string
  registration_endpoint: string
  response_type: string
  revocation_endpoint: string
  signing_algorithm: string
  token_endpoint: string
  userinfo_endpoint: string
}

export type OidcClientStatus = 'active' | 'inactive'
export type OidcClientKind = 'mcp_oauth' | 'oidc_embed'
export type OidcClientScopeAssignmentType = 'default' | 'optional'

export interface OAuthResource {
  allowed_client_scope_ids: string[]
  allowed_client_scopes: OAuthResourceScopeAssignment[]
  created_at: string | null
  id: string
  introspection_client_id: string
  name: string
  resource_uri: string
  status: OidcClientStatus
  updated_at: string | null
}

export interface OAuthResourceScopeAssignment {
  client_scope_id: string
  display_name: string
  scope_name: string
  system: boolean
}

export type OAuthResourcePayload = Pick<
  OAuthResource,
  'allowed_client_scope_ids' | 'name' | 'resource_uri' | 'status'
>

export interface OAuthResourceCreated extends OAuthResource {
  introspection_secret: string
}

export interface OidcClientApplication {
  id: string
  name: string
  status: string
}

export interface OidcApplicationOption extends OidcClientApplication {
  bound_client_record_id: string | null
}

export interface OidcClientScopeAssignment {
  assignment_type: OidcClientScopeAssignmentType
  client_scope_id: string
  display_name: string
  scope_name: string
  system: boolean
}

export interface OidcClient {
  application_ids: string[]
  applications: OidcClientApplication[]
  client_id: string
  client_kind: OidcClientKind
  client_scope_assignments: OidcClientScopeAssignment[]
  created_at: string | null
  default_client_scope_ids: string[]
  id: string
  name: string
  optional_client_scope_ids: string[]
  redirect_uris: string[]
  resource_id: string | null
  resource_uri: string | null
  status: OidcClientStatus
  token_endpoint_auth_method: 'client_secret_basic' | 'client_secret_post'
  updated_at: string | null
}

export type OidcClientPayload = Pick<
  OidcClient,
  | 'application_ids'
  | 'client_kind'
  | 'default_client_scope_ids'
  | 'name'
  | 'optional_client_scope_ids'
  | 'redirect_uris'
  | 'resource_id'
  | 'status'
  | 'token_endpoint_auth_method'
>

export interface OidcClientCreated extends OidcClient {
  client_secret: string
}

export type OidcClaimValueType = 'boolean' | 'number' | 'string' | 'string_array'
export type OidcProtocolMapperType = 'hardcoded_claim' | 'identity_source'

export interface OidcProtocolMapper {
  add_to_id_token: boolean
  add_to_introspection: boolean
  add_to_userinfo: boolean
  claim_json_type: OidcClaimValueType
  client_scope_id: string
  enabled: boolean
  hardcoded_value: unknown
  id: string
  mapper_type: OidcProtocolMapperType
  name: string
  source: string | null
  system: boolean
  token_claim_name: string
}

export interface OidcClientScope {
  description: string
  display_name: string
  enabled: boolean
  id: string
  include_in_token_scope: boolean
  mapper_count: number
  name: string
  system: boolean
}

export interface OidcClientScopeDetail extends OidcClientScope {
  mappers: OidcProtocolMapper[]
}

export type OidcClientScopePayload = Pick<
  OidcClientScope,
  'description' | 'display_name' | 'enabled' | 'include_in_token_scope' | 'name'
>

export type OidcProtocolMapperPayload = Pick<
  OidcProtocolMapper,
  | 'add_to_id_token'
  | 'add_to_introspection'
  | 'add_to_userinfo'
  | 'claim_json_type'
  | 'enabled'
  | 'hardcoded_value'
  | 'mapper_type'
  | 'name'
  | 'source'
  | 'token_claim_name'
>

export interface OidcClaimSource {
  key: string
  label: string
  origin: 'account' | 'account_attribute' | 'organization' | 'organization_attribute'
  source: string
  value_type: OidcClaimValueType
}

export type BrandAssetType = 'favicon' | 'logo'

export interface BrandAssetMetadata {
  content_type?: string | null
  key?: string | null
  size?: number | null
  updated_at?: string | null
  url?: string | null
}

export type WorkspaceBackgroundVariant = 'desktop' | 'laptop' | 'mobile'

export interface WorkspaceBackgroundCrop {
  focus_x: number
  focus_y: number
  zoom: number
}

export interface WorkspaceBackgroundVariantMetadata {
  focus_x?: number | null
  focus_y?: number | null
  height?: number | null
  jpeg_key?: string | null
  jpeg_url?: string | null
  webp_key?: string | null
  webp_url?: string | null
  width?: number | null
}

export interface WorkspaceBackgroundManifest {
  base_color?: string | null
  enabled?: boolean
  image_opacity?: number | null
  intensity_preset?: 'clear' | 'standard' | 'subtle'
  mobile_conversation_mode?: 'background' | 'base_color'
  updated_at?: string | null
  variants?: Partial<Record<WorkspaceBackgroundVariant, WorkspaceBackgroundVariantMetadata>>
  version?: string | null
}

export interface WorkspaceBackgroundSourceMetadata {
  content_type?: string | null
  height?: number | null
  key?: string | null
  size?: number | null
  width?: number | null
}

export interface WorkspaceBackgroundSourceConfig {
  crops?: Partial<Record<WorkspaceBackgroundVariant, WorkspaceBackgroundCrop>>
  mobile_override?: WorkspaceBackgroundSourceMetadata | null
  primary?: WorkspaceBackgroundSourceMetadata | null
}

export interface BrandingSettingsConfig {
  brand_assets?: Partial<Record<BrandAssetType, BrandAssetMetadata>>
  business_navigation?: BusinessNavigationSettings | null
  chat_user_bubble_color?: string | null
  description?: string | null
  favicon?: string | null
  hub_agent_name?: string | null
  hub_agent_welcome_title?: string | null
  logo?: string | null
  title?: string | null
  workspace_background?: WorkspaceBackgroundManifest | null
  workspace_background_source?: WorkspaceBackgroundSourceConfig | null
}

export interface BusinessNavigationSettings {
  application_id?: string | null
  enabled: boolean
  label: string
  url: string
}

export interface AdminSystemSettings {
  config: BrandingSettingsConfig
  created_at?: string | null
  id: string | null
  last_updated_by?: string | null
  updated_at?: string | null
}

export interface MCPRuntimeSettings {
  discovery_timeout_ms: number
  execution_timeout_ms: number
  max_discovery_response_bytes: number
  max_execution_response_bytes: number
  private_network_allowlist: string[]
  revision: number
  updated_at: string | null
  updated_by: string | null
}

export interface MCPRuntimeSettingsUpdatePayload extends Omit<
  MCPRuntimeSettings,
  'revision' | 'updated_at' | 'updated_by'
> {
  expected_revision: number
}

export interface BrandingSettingsPayload {
  chatUserBubbleColor: string
  description: string
  hubAgentName: string
  hubAgentWelcomeTitle: string
  title: string
}

export type BusinessNavigationSettingsPayload = BusinessNavigationSettings

export interface WorkspaceBackgroundPublishPayload {
  baseColor: string
  crops: Record<WorkspaceBackgroundVariant, WorkspaceBackgroundCrop>
  enabled: boolean
  imageOpacity: number
  mobileConversationMode: 'background' | 'base_color'
  mobileFile?: File | null
  primaryFile?: File | null
  removeMobileOverride: boolean
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

export async function listAdminAccounts({
  order = 'desc',
  orderBy = 'name',
  page,
  pageSize,
  search,
  status,
}: AdminAccountsParams) {
  const params = new URLSearchParams({
    order,
    order_by: orderBy,
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'search', search)
  appendOptionalQuery(params, 'status', status)

  const response = await apiClient.request<SuccessEnvelope<AdminAccountsData>>(
    `/admin/settings/admins?${params.toString()}`,
  )

  return response.data
}

export async function createAdminAccount(values: {
  email: string
  mustChangePassword: boolean
  name: string
}) {
  const response = await apiClient.request<SuccessEnvelope<AdminAccountCreateResult>>(
    '/admin/settings/admins',
    {
      body: JSON.stringify({
        email: values.email.trim(),
        must_change_password: values.mustChangePassword,
        name: values.name.trim(),
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function updateAdminAccount(values: {
  adminId: string
  name?: string
  status?: 'active' | 'banned'
}) {
  const body: Record<string, string> = {}
  if (values.name !== undefined) {
    body.name = values.name.trim()
  }
  if (values.status !== undefined) {
    body.status = values.status
  }

  const response = await apiClient.request<SuccessEnvelope<AdminAccount>>(
    `/admin/settings/admins/${encodeURIComponent(values.adminId)}`,
    {
      body: JSON.stringify(body),
      method: 'PUT',
    },
  )

  return response.data
}

export async function resetAdminAccountPassword(values: { adminId: string }) {
  const response = await apiClient.request<OptionalDataEnvelope<AdminAccountPasswordResetResult>>(
    `/admin/settings/admins/${encodeURIComponent(values.adminId)}/password`,
    {
      body: JSON.stringify({}),
      method: 'PUT',
    },
  )

  return response.data ?? {}
}

export async function getCoreAgentSettings() {
  const response = await apiClient.request<SuccessEnvelope<CoreAgentSettings>>(
    '/admin/core-agent/settings',
  )

  return response.data
}

export async function updateCoreAgentSettings(values: CoreAgentSettingsPayload) {
  const response = await apiClient.request<SuccessEnvelope<CoreAgentSettings>>(
    '/admin/core-agent/settings',
    {
      body: JSON.stringify(values),
      method: 'PUT',
    },
  )

  return response.data
}

export async function getAdminSsoConfig() {
  const response = await apiClient.request<SuccessEnvelope<AdminSsoConfig>>('/admin/sso')

  return response.data
}

export async function listSsoIdentityMatchSources() {
  const response = await apiClient.request<SuccessEnvelope<{ items: SsoIdentityMatchSource[] }>>(
    '/admin/sso/identity-match-sources',
  )

  return response.data.items
}

export async function updateAdminSsoConfig(values: AdminSsoConfigPayload) {
  const response = await apiClient.request<SuccessEnvelope<AdminSsoConfig>>('/admin/sso', {
    body: JSON.stringify(values),
    method: 'PUT',
  })

  return response.data
}

export async function testAdminSsoConfig() {
  const response = await apiClient.request<SuccessEnvelope<AdminSsoTestResult>>('/admin/sso/test', {
    method: 'POST',
  })

  return response.data
}

export async function getOidcProviderConfiguration() {
  const response = await apiClient.request<SuccessEnvelope<OidcProviderConfiguration>>('/admin/idp')
  return response.data
}

export async function updateOidcProviderConfiguration(enabled: boolean) {
  const response = await apiClient.request<SuccessEnvelope<OidcProviderConfiguration>>(
    '/admin/idp',
    { body: JSON.stringify({ enabled }), method: 'PUT' },
  )
  return response.data
}

export async function rotateOidcSigningKey() {
  const response = await apiClient.request<
    SuccessEnvelope<{ algorithm: string; kid: string; status: string }>
  >('/admin/idp/signing-keys/rotate', { method: 'POST' })
  return response.data
}

export async function listOidcClients() {
  const response =
    await apiClient.request<SuccessEnvelope<{ items: OidcClient[] }>>('/admin/idp/clients')
  return response.data.items
}

export async function listOAuthResources() {
  const response = await apiClient.request<SuccessEnvelope<{ items: OAuthResource[] }>>(
    '/admin/idp/oauth-resources',
  )
  return response.data.items
}

export async function createOAuthResource(payload: OAuthResourcePayload) {
  const response = await apiClient.request<SuccessEnvelope<OAuthResourceCreated>>(
    '/admin/idp/oauth-resources',
    { body: JSON.stringify(payload), method: 'POST' },
  )
  return response.data
}

export async function updateOAuthResource(values: {
  payload: OAuthResourcePayload
  resourceId: string
}) {
  const response = await apiClient.request<SuccessEnvelope<OAuthResource>>(
    `/admin/idp/oauth-resources/${encodeURIComponent(values.resourceId)}`,
    { body: JSON.stringify(values.payload), method: 'PUT' },
  )
  return response.data
}

export async function deleteOAuthResource(resourceId: string) {
  await apiClient.request<SuccessEnvelope<null>>(
    `/admin/idp/oauth-resources/${encodeURIComponent(resourceId)}`,
    { method: 'DELETE' },
  )
}

export async function rotateOAuthResourceSecret(resourceId: string) {
  const response = await apiClient.request<SuccessEnvelope<OAuthResourceCreated>>(
    `/admin/idp/oauth-resources/${encodeURIComponent(resourceId)}/rotate-secret`,
    { method: 'POST' },
  )
  return response.data
}

export async function getOidcClient(clientRecordId: string) {
  const response = await apiClient.request<SuccessEnvelope<OidcClient>>(
    `/admin/idp/clients/${encodeURIComponent(clientRecordId)}`,
  )
  return response.data
}

export async function createOidcClient(payload: OidcClientPayload) {
  const response = await apiClient.request<SuccessEnvelope<OidcClientCreated>>(
    '/admin/idp/clients',
    { body: JSON.stringify(payload), method: 'POST' },
  )
  return response.data
}

export async function updateOidcClient(values: {
  clientRecordId: string
  payload: OidcClientPayload
}) {
  const response = await apiClient.request<SuccessEnvelope<OidcClient>>(
    `/admin/idp/clients/${encodeURIComponent(values.clientRecordId)}`,
    { body: JSON.stringify(values.payload), method: 'PUT' },
  )
  return response.data
}

export async function deleteOidcClient(clientRecordId: string) {
  await apiClient.request<SuccessEnvelope<null>>(
    `/admin/idp/clients/${encodeURIComponent(clientRecordId)}`,
    { method: 'DELETE' },
  )
}

export async function rotateOidcClientSecret(clientRecordId: string) {
  const response = await apiClient.request<SuccessEnvelope<OidcClientCreated>>(
    `/admin/idp/clients/${encodeURIComponent(clientRecordId)}/rotate-secret`,
    { method: 'POST' },
  )
  return response.data
}

export async function listOidcApplicationOptions() {
  const response = await apiClient.request<SuccessEnvelope<{ items: OidcApplicationOption[] }>>(
    '/admin/idp/application-options',
  )
  return response.data.items
}

export async function listOidcClaimSources() {
  const response = await apiClient.request<SuccessEnvelope<{ items: OidcClaimSource[] }>>(
    '/admin/idp/claim-sources',
  )
  return response.data.items
}

export async function listOidcClientScopes() {
  const response = await apiClient.request<SuccessEnvelope<{ items: OidcClientScope[] }>>(
    '/admin/idp/client-scopes',
  )
  return response.data.items
}

export async function getOidcClientScope(clientScopeId: string) {
  const response = await apiClient.request<SuccessEnvelope<OidcClientScopeDetail>>(
    `/admin/idp/client-scopes/${encodeURIComponent(clientScopeId)}`,
  )
  return response.data
}

export async function createOidcClientScope(payload: OidcClientScopePayload) {
  const response = await apiClient.request<SuccessEnvelope<OidcClientScope>>(
    '/admin/idp/client-scopes',
    { body: JSON.stringify(payload), method: 'POST' },
  )
  return response.data
}

export async function updateOidcClientScope(values: {
  clientScopeId: string
  payload: OidcClientScopePayload
}) {
  const response = await apiClient.request<SuccessEnvelope<OidcClientScope>>(
    `/admin/idp/client-scopes/${encodeURIComponent(values.clientScopeId)}`,
    { body: JSON.stringify(values.payload), method: 'PUT' },
  )
  return response.data
}

export async function deleteOidcClientScope(clientScopeId: string) {
  await apiClient.request<SuccessEnvelope<null>>(
    `/admin/idp/client-scopes/${encodeURIComponent(clientScopeId)}`,
    { method: 'DELETE' },
  )
}

export async function createOidcProtocolMapper(values: {
  clientScopeId: string
  payload: OidcProtocolMapperPayload
}) {
  const response = await apiClient.request<SuccessEnvelope<OidcProtocolMapper>>(
    `/admin/idp/client-scopes/${encodeURIComponent(values.clientScopeId)}/mappers`,
    { body: JSON.stringify(values.payload), method: 'POST' },
  )
  return response.data
}

export async function updateOidcProtocolMapper(values: {
  clientScopeId: string
  mapperId: string
  payload: OidcProtocolMapperPayload
}) {
  const response = await apiClient.request<SuccessEnvelope<OidcProtocolMapper>>(
    `/admin/idp/client-scopes/${encodeURIComponent(values.clientScopeId)}/mappers/${encodeURIComponent(values.mapperId)}`,
    { body: JSON.stringify(values.payload), method: 'PUT' },
  )
  return response.data
}

export async function deleteOidcProtocolMapper(values: {
  clientScopeId: string
  mapperId: string
}) {
  await apiClient.request<SuccessEnvelope<null>>(
    `/admin/idp/client-scopes/${encodeURIComponent(values.clientScopeId)}/mappers/${encodeURIComponent(values.mapperId)}`,
    { method: 'DELETE' },
  )
}

export async function getBrandingSettings() {
  const response =
    await apiClient.request<SuccessEnvelope<AdminSystemSettings>>('/admin/sys/settings')

  return response.data
}

export async function getMCPRuntimeSettings() {
  const response = await apiClient.request<SuccessEnvelope<MCPRuntimeSettings>>(
    '/admin/mcp-runtime-settings',
  )
  return response.data
}

export async function updateMCPRuntimeSettings(payload: MCPRuntimeSettingsUpdatePayload) {
  const response = await apiClient.request<SuccessEnvelope<MCPRuntimeSettings>>(
    '/admin/mcp-runtime-settings',
    { body: JSON.stringify(payload), method: 'PUT' },
  )
  return response.data
}

export async function updateBrandingSettings(values: BrandingSettingsPayload) {
  const response = await apiClient.request<SuccessEnvelope<AdminSystemSettings>>(
    '/admin/sys/settings',
    {
      body: JSON.stringify({
        chat_user_bubble_color: values.chatUserBubbleColor.trim().toUpperCase(),
        description: values.description.trim(),
        hub_agent_name: values.hubAgentName.trim(),
        hub_agent_welcome_title: values.hubAgentWelcomeTitle.trim(),
        title: values.title.trim(),
      }),
      method: 'PUT',
    },
  )

  return response.data
}

export async function updateBusinessNavigationSettings(values: BusinessNavigationSettingsPayload) {
  const response = await apiClient.request<SuccessEnvelope<AdminSystemSettings>>(
    '/admin/sys/settings',
    {
      body: JSON.stringify({
        business_navigation: {
          enabled: values.enabled,
          label: values.label.trim(),
          url: values.url.trim(),
          application_id: values.application_id ?? null,
        },
      }),
      method: 'PUT',
    },
  )

  return response.data
}

export async function uploadBrandAsset(values: { assetType: BrandAssetType; file: File }) {
  const body = new FormData()
  body.set('file', values.file)

  const response = await apiClient.request<SuccessEnvelope<AdminSystemSettings>>(
    `/admin/sys/settings/brand-assets/${values.assetType}`,
    {
      body,
      method: 'POST',
    },
  )

  return response.data
}

export async function deleteBrandAsset(assetType: BrandAssetType) {
  const response = await apiClient.request<SuccessEnvelope<AdminSystemSettings>>(
    `/admin/sys/settings/brand-assets/${assetType}`,
    {
      method: 'DELETE',
    },
  )

  return response.data
}

export async function publishWorkspaceBackground(values: WorkspaceBackgroundPublishPayload) {
  const body = new FormData()
  body.set(
    'config',
    JSON.stringify({
      base_color: values.baseColor,
      crops: values.crops,
      enabled: values.enabled,
      image_opacity: values.imageOpacity,
      mobile_conversation_mode: values.mobileConversationMode,
      remove_mobile_override: values.removeMobileOverride,
    }),
  )
  if (values.primaryFile) {
    body.set('primary_file', values.primaryFile)
  }
  if (values.mobileFile) {
    body.set('mobile_file', values.mobileFile)
  }

  const response = await apiClient.request<SuccessEnvelope<AdminSystemSettings>>(
    '/admin/sys/settings/workspace-background',
    { body, method: 'PUT' },
  )
  return response.data
}

export async function deleteWorkspaceBackground() {
  const response = await apiClient.request<SuccessEnvelope<AdminSystemSettings>>(
    '/admin/sys/settings/workspace-background',
    { method: 'DELETE' },
  )
  return response.data
}

export async function getWorkspaceBackgroundSourceBlob(sourceType: 'mobile_override' | 'primary') {
  return await apiClient.requestBlob(
    `/admin/sys/settings/workspace-background/source/${sourceType}`,
  )
}
